// Unread counts — no postgres_changes, no direct Supabase table reads.
// Primary update path: Realtime Broadcast events that carry count deltas or
// absolute totals. Fallback: periodic poll + focus/visibility refetch.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@shared/api/supabaseClient';
import { fetchUnreadCounts } from '@shared/api/messaging';
import { useAuthStore } from '@shared/stores/auth-store';

type UnreadCounts = {
  unreadMessages: number;
  unreadConversations: number;
};

type UseUnreadCountsOptions = {
  userId?: string;
  enabled?: boolean;
};

// Long interval — realtime handles incremental updates; this is purely a
// safety-net reconciliation for clients that were offline or missed events.
const POLL_INTERVAL_MS = 60_000;
const USER_NOTIFICATION_CHANNEL = (userId: string) => `messaging-user:${userId}`;

export function useUnreadCounts({
  userId: _userId,
  enabled = true,
}: UseUnreadCountsOptions = {}) {
  const authContext = useAuthStore((s) => s.authContext);
  const user = useAuthStore((s) => s.user);

  const [counts, setCounts] = useState<UnreadCounts>({ unreadMessages: 0, unreadConversations: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const role = (() => {
    const userType = authContext?.userType;
    if (userType === 'patient') return 'patient' as const;
    if (userType === 'doctor') return 'doctor' as const;
    return 'admin' as const;
  })();

  const actorUserId = authContext?.userId ?? authContext?.authUserId ?? user?.id ?? null;

  const refresh = useCallback(async () => {
    if (!enabled || !authContext) {
      setCounts({ unreadMessages: 0, unreadConversations: 0 });
      setLoading(false);
      return;
    }

    try {
      const data = await fetchUnreadCounts(role);
      setCounts(data);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(new Error(`Failed to load unread counts: ${msg}`));
    } finally {
      setLoading(false);
    }
  }, [enabled, authContext, role]);

  // Initial fetch + refetch on focus/visibility (reconciliation only).
  useEffect(() => {
    void refresh();

    const onFocus = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  // Safety-net poll — fires much less frequently now that realtime carries the load.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  // Realtime Broadcast — patch state immediately without hitting the API.
  useEffect(() => {
    if (!enabled || !actorUserId) return;

    if (channelRef.current) {
      void supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(USER_NOTIFICATION_CHANNEL(actorUserId), {
        config: { private: true },
      })
      .on('broadcast', { event: 'message' }, ({ payload }) => {
        const eventName = typeof payload?.event === 'string' ? payload.event : '';

        if (eventName === 'message.created') {
          // Sender sees their own message.created — ignore for unread counts.
          const senderId = typeof payload?.senderId === 'string' ? payload.senderId : null;
          if (senderId === actorUserId) return;

          // New message from someone else — increment both counters optimistically.
          // The 60s poll reconciles any drift.
          setCounts((prev) => ({
            unreadMessages: prev.unreadMessages + 1,
            // We don't know if this conversation was previously at 0 unread,
            // so we cap conversations at unreadMessages+1 to avoid overcounting.
            unreadConversations: Math.min(
              prev.unreadConversations + 1,
              prev.unreadMessages + 1,
            ),
          }));
          return;
        }

        if (eventName === 'unread.updated') {
          const { newTotalUnreadMessages, newTotalUnreadConversations, delta } =
            payload as Record<string, unknown>;

          if (
            typeof newTotalUnreadMessages === 'number' &&
            typeof newTotalUnreadConversations === 'number'
          ) {
            // Authoritative totals from backend (sent after mark-read).
            setCounts({
              unreadMessages: newTotalUnreadMessages,
              unreadConversations: newTotalUnreadConversations,
            });
          } else if (typeof delta === 'number' && delta > 0) {
            setCounts((prev) => ({
              unreadMessages: prev.unreadMessages + delta,
              unreadConversations: Math.min(
                prev.unreadConversations + 1,
                prev.unreadMessages + delta,
              ),
            }));
          }
          return;
        }
      })
      .on('broadcast', { event: 'presence' }, () => {
        // participant.read for self — the unread.updated with totals is sent on
        // the same user channel separately; no action needed here.
      });

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        // Reconnect — reconcile from API.
        void refresh();
      }
    });
    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        void supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, actorUserId, refresh]);

  return { unreadCounts: counts, loading, error };
}
